import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';
import { usersService, User } from '@/services/users.service';
import UserModal from './UserModal';
import DeleteUserDialog from './DeleteUserDialog';
import toast from 'react-hot-toast';

export default function UserList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);

  // Fetch users with react-query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['users', page, searchQuery],
    queryFn: () =>
      usersService.getUsers({
        page,
        limit: 25,
        search: searchQuery ?? undefined,
      }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    refetch();
    toast.success('User created successfully');
  };

  const handleEditSuccess = () => {
    setEditingUser(null);
    refetch();
    toast.success('User updated successfully');
  };

  const handleDeleteSuccess = () => {
    setDeletingUser(null);
    refetch();
    toast.success('User deleted successfully');
  };

  if (error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorMessage: string = (error as any)?.response?.data?.error?.message
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ?? (error as any)?.message
      ?? 'Failed to load users';
    const isPermissionError =
      errorMessage.includes('permission') ||
      errorMessage.includes('INSUFFICIENT_PERMISSIONS') ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error as any)?.response?.status === 403;

    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center py-12">
            <XCircleIcon className="w-16 h-16 mx-auto text-danger-500 mb-4" />
            <p className="text-danger-600 dark:text-danger-400 font-medium mb-2">
              {isPermissionError ? 'Access Denied' : 'Failed to Load Users'}
            </p>
            <p className="text-secondary-600 dark:text-secondary-400 text-sm">
              {isPermissionError
                ? 'You do not have permission to view users. Please contact your administrator.'
                : errorMessage}
            </p>
            {!isPermissionError && (
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                Try Again
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>User Management</CardTitle>
              <CardDescription>
                Manage user accounts, roles, and permissions
              </CardDescription>
            </div>
            <Button onClick={() => setIsCreateModalOpen(true)}>
              Create User
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6">
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
              <Input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </form>

          {/* Users Table */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
            </div>
          ) : data?.users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-secondary-600 dark:text-secondary-400">
                {searchQuery ? 'No users found matching your search.' : 'No users found.'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary-50 dark:bg-secondary-800 border-b border-secondary-200 dark:border-secondary-700">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        Roles
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        Last Login
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-secondary-700 dark:text-secondary-300 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-secondary-900 divide-y divide-secondary-200 dark:divide-secondary-700">
                    {data?.users.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                              <span className="text-primary-700 dark:text-primary-300 font-semibold text-sm">
                                {user.firstName[0]}{user.lastName[0]}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-secondary-900 dark:text-white">
                                {user.fullName}
                              </div>
                              {user.phone && (
                                <div className="text-sm text-secondary-500 dark:text-secondary-400">
                                  {user.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-secondary-900 dark:text-white">
                            {user.email}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            {user.emailVerified && (
                              <span className="inline-flex items-center text-xs text-success-600 dark:text-success-400">
                                <CheckCircleIcon className="w-3 h-3 mr-1" />
                                Verified
                              </span>
                            )}
                            {user.mfaEnabled && (
                              <span className="inline-flex items-center text-xs text-primary-600 dark:text-primary-400">
                                <ShieldCheckIcon className="w-3 h-3 mr-1" />
                                MFA
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {user.roles.map((role) => (
                              <span
                                key={role.id}
                                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300"
                              >
                                {usersService.getRoleDisplayName(role)}
                              </span>
                            ))}
                            {user.roles.length === 0 && (
                              <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                No roles
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span
                            className={cn(
                              'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                              user.isActive
                                ? user.emailVerified
                                  ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
                                  : 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300'
                                : 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300'
                            )}
                          >
                            {usersService.getUserStatusText(user)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-secondary-500 dark:text-secondary-400">
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleDateString()
                            : 'Never'}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => setEditingUser(user)}
                              className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 p-1 rounded hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                              title="Edit user"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => setDeletingUser(user)}
                              className="text-danger-600 hover:text-danger-900 dark:text-danger-400 dark:hover:text-danger-300 p-1 rounded hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                              title="Delete user"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {data && data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6 pt-6 border-t border-secondary-200 dark:border-secondary-700">
                  <div className="text-sm text-secondary-700 dark:text-secondary-300">
                    Showing page {data.pagination.page} of {data.pagination.totalPages}
                    {' '}({data.pagination.total} total users)
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= data.pagination.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit User Modal */}
      {(isCreateModalOpen || editingUser) && (
        <UserModal
          user={editingUser}
          isOpen={isCreateModalOpen || !!editingUser}
          onClose={() => {
            setIsCreateModalOpen(false);
            setEditingUser(null);
          }}
          onSuccess={editingUser ? handleEditSuccess : handleCreateSuccess}
        />
      )}

      {/* Delete User Dialog */}
      {deletingUser && (
        <DeleteUserDialog
          user={deletingUser}
          isOpen={!!deletingUser}
          onClose={() => setDeletingUser(null)}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </>
  );
}
